/**
 * Small UI helpers: DOM, icons (Lucide sprite), number formatting (vi-VN),
 * toasts, clipboard, tooltips, timing utilities.
 */

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);

/** Inline reference to a Lucide symbol in /assets/b/icons-d8baf343d1.svg. */
export const icon = (name, cls = '') =>
  `<svg class="i${cls ? ' ' + cls : ''}" aria-hidden="true"><use href="/assets/b/icons-d8baf343d1.svg#i-${name}"/></svg>`;

/* ---------------- numbers (Vietnamese conventions: 1.234,5) ---------------- */

const NF = new Map();
function nf(min, max) {
  const k = min + ':' + max;
  if (!NF.has(k)) NF.set(k, new Intl.NumberFormat('vi-VN', { minimumFractionDigits: min, maximumFractionDigits: max }));
  return NF.get(k);
}
export const fmt = {
  num: (n, d = 0, min = 0) => (Number.isFinite(+n) ? nf(min, d).format(+n) : '—'),
  /** area with adaptive unit; returns HTML-safe text */
  area(m2, { html = false, ha = true } = {}) {
    if (!Number.isFinite(m2)) return '—';
    let v, u;
    if (ha && m2 >= 1e6) { v = fmt.num(m2 / 1e6, 2); u = 'km²'; }
    else if (ha && m2 >= 1e4) { v = fmt.num(m2 / 1e4, 2); u = 'ha'; }
    else { v = fmt.num(m2, m2 < 100 ? 2 : 1); u = 'm²'; }
    return html ? `${v}<small>${u}</small>` : `${v} ${u}`;
  },
  m2: (m2, d = 1) => (Number.isFinite(m2) ? `${fmt.num(m2, d)} m²` : '—'),
  dist(m, { html = false } = {}) {
    if (!Number.isFinite(m)) return '—';
    const [v, u] = m >= 1000 ? [fmt.num(m / 1000, m >= 10000 ? 1 : 2), 'km'] : [fmt.num(m, m < 10 ? 2 : m < 100 ? 1 : 0), 'm'];
    return html ? `${v}<small>${u}</small>` : `${v} ${u}`;
  },
  pct: (p) => (Number.isFinite(p) ? `${fmt.num(p, p < 1 ? 2 : 1)}%` : '—'),
  ll: (v, d = 6) => (+v).toFixed(d),
  ms: (ms) => (ms < 1000 ? `${Math.round(ms)} ms` : `${fmt.num(ms / 1000, 1)} s`),
};

/* ---------------- environment ---------------- */

const mqMobile = matchMedia('(max-width: 760px)');
const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
const mqHover = matchMedia('(hover: hover) and (pointer: fine)');
export const isMobile = () => mqMobile.matches;
export const reducedMotion = () => mqReduce.matches;
export const canHover = () => mqHover.matches;
export const onMobileChange = (fn) => mqMobile.addEventListener('change', () => fn(mqMobile.matches));

/* ---------------- timing ---------------- */

export function debounce(fn, ms) {
  let t;
  const d = (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  d.cancel = () => clearTimeout(t);
  d.flush = (...a) => { clearTimeout(t); fn(...a); };
  return d;
}
export function rafThrottle(fn) {
  let pending = null, id = 0;
  return (...a) => {
    pending = a;
    if (!id) id = requestAnimationFrame(() => { id = 0; const p = pending; pending = null; fn(...p); });
  };
}
export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));

/* ---------------- toasts ---------------- */

const TOAST_ICON = { ok: 'check', err: 'circle-alert', info: 'info', warn: 'triangle-alert' };
/**
 * A modal <dialog> makes the rest of the page inert and dims it, so a toast raised while one is
 * open (copy link, delete + undo…) lives inside that dialog instead of the page-level host.
 */
function toastHost() {
  let dlg = null;
  try { dlg = [...document.querySelectorAll('dialog[open]')].reverse().find((d) => d.matches(':modal')); } catch { /* :modal unsupported */ }
  if (!dlg) return $('#toasts');
  let host = dlg.querySelector(':scope > .toasts');
  if (!host) {
    host = document.createElement('div');
    host.className = 'toasts in-modal';
    host.setAttribute('role', 'status');
    host.setAttribute('aria-live', 'polite');
    dlg.appendChild(host);
  }
  return host;
}

export function toast(message, { type = 'info', ms = 2600, action, actions } = {}) {
  const host = toastHost();
  if (!host) return () => {};
  const acts = actions || (action ? [action] : []);
  while (host.children.length >= 3) host.firstElementChild.remove();
  const el = document.createElement('div');
  el.className = `toast ${type === 'warn' ? 'err' : type}`;
  el.innerHTML = `${icon(TOAST_ICON[type] || 'info')}<span>${esc(message)}</span>${acts.map((a, i) => `<button type="button" class="toast-act" data-i="${i}">${esc(a.label)}</button>`).join('')}`;
  host.appendChild(el);
  for (const b of el.querySelectorAll('.toast-act')) b.addEventListener('click', (e) => { e.stopPropagation(); acts[+b.dataset.i].onClick?.(); close(); });
  const close = () => {
    if (el.classList.contains('out')) return;
    el.classList.add('out');
    el.addEventListener('animationend', () => el.remove(), { once: true });
    setTimeout(() => el.remove(), 400);
  };
  el.addEventListener('click', close);
  setTimeout(close, ms);
  return close;
}

/**
 * Load a feature module on demand. After a new deployment the old chunk names are gone: ask for a
 * reload instead of failing silently (or say the connection is down).
 */
export async function lazyImport(load) {
  try { return await load(); }
  catch (e) {
    if (navigator.onLine === false) toast('Không có kết nối mạng, chưa mở được chức năng này', { type: 'warn', ms: 4000 });
    else toast('Ứng dụng vừa được cập nhật. Tải lại trang để dùng chức năng này.', { type: 'warn', ms: 10000, action: { label: 'Tải lại', onClick: () => location.reload() } });
    throw e;
  }
}

/* ---------------- clipboard ---------------- */

export async function copyText(text, what = 'nội dung') {
  try {
    if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
    else {
      const ta = document.createElement('textarea');
      ta.value = text; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      if (!ok) throw new Error('copy failed');
    }
    toast(`Đã sao chép ${what}`, { type: 'ok', ms: 1800 });
    return true;
  } catch {
    toast('Trình duyệt không cho phép sao chép', { type: 'err' });
    return false;
  }
}

/* ---------------- tooltips ([data-tip]) ---------------- */

export function initTooltips() {
  const tip = $('#tooltip');
  if (!tip) return;
  let target = null, timer = 0;
  // inside a modal <dialog> the tip must join the top layer, or the dialog would cover it
  const canPop = typeof tip.showPopover === 'function';
  const unpop = () => {
    if (!canPop || !tip.hasAttribute('popover')) return;
    try { if (tip.matches(':popover-open')) tip.hidePopover(); } catch { /* not open */ }
    tip.removeAttribute('popover');
  };
  const hide = () => { clearTimeout(timer); target = null; tip.hidden = true; unpop(); };
  const place = (el) => {
    const text = el.getAttribute('data-tip');
    if (!text || !document.contains(el)) return hide();
    tip.textContent = text;
    tip.hidden = false;
    if (canPop && el.closest('dialog[open]')) {
      try { tip.setAttribute('popover', 'manual'); tip.showPopover(); } catch { unpop(); }
    } else unpop();
    const r = el.getBoundingClientRect();
    const tw = tip.offsetWidth, th = tip.offsetHeight, vw = innerWidth, vh = innerHeight, m = 8;
    let x, y;
    if (r.left > vw * 0.72) { x = r.left - tw - m; y = r.top + r.height / 2 - th / 2; } // right-edge controls: show on the left
    else if (r.bottom + th + m < vh) { x = r.left + r.width / 2 - tw / 2; y = r.bottom + m; }
    else { x = r.left + r.width / 2 - tw / 2; y = r.top - th - m; }
    tip.style.left = Math.round(Math.max(6, Math.min(vw - tw - 6, x))) + 'px';
    tip.style.top = Math.round(Math.max(6, Math.min(vh - th - 6, y))) + 'px';
  };
  document.addEventListener('pointerover', (e) => {
    if (!canHover()) return;
    const el = e.target.closest?.('[data-tip]');
    if (el === target) return;
    hide();
    if (!el) return;
    target = el;
    timer = setTimeout(() => target === el && place(el), 380);
  });
  document.addEventListener('pointerdown', hide, true);
  document.addEventListener('keydown', hide, true);
  addEventListener('blur', hide);
  document.addEventListener('scroll', hide, true);
}

/* ---------------- misc ---------------- */

/**
 * Scroll affordance for a scroll area: toggles .scrolled (content above) and .more-below
 * (content below) on `host`, so the CSS can draw a divider under the header and fade the
 * bottom edge — long lists never look cut off. Re-checks on scroll and on resize/content change.
 */
export function scrollHints(scroller, host = scroller.parentElement) {
  if (!scroller || scroller.__hints) return;
  scroller.__hints = true;
  const sync = () => {
    const max = scroller.scrollHeight - scroller.clientHeight;
    host.classList.toggle('scrolled', scroller.scrollTop > 2);
    host.classList.toggle('more-below', max > 4 && scroller.scrollTop < max - 4);
  };
  scroller.addEventListener('scroll', rafThrottle(sync), { passive: true });
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(rafThrottle(sync));
    ro.observe(scroller);
    new MutationObserver(rafThrottle(() => { sync(); for (const c of scroller.children) ro.observe(c); })).observe(scroller, { childList: true });
    for (const c of scroller.children) ro.observe(c);
  }
  sync();
  return sync;
}

/**
 * Frames that clip a scroll area (drawer, panel, tray, cards, dialogs) must never scroll themselves.
 * Browsers scroll even an overflow:hidden box to reveal a focused control or a scrollIntoView()
 * target, which slides the whole card out of its frame (header gone, blank card). The CSS uses
 * overflow:clip; this undoes such a scroll in browsers without it (Safari < 16).
 */
const CLIP_FRAMES = '.drawer, .panel, .tray, .card, .card-inner, .help-modal, .catalog-modal';
export function guardClipFrames() {
  const reset = (el) => { if (el.scrollTop || el.scrollLeft) { el.scrollTop = 0; el.scrollLeft = 0; } };
  document.addEventListener('scroll', (e) => { if (e.target instanceof Element && e.target.matches(CLIP_FRAMES)) reset(e.target); }, { capture: true, passive: true });
  // focus scrolls happen synchronously; re-check the frames around the focused control next frame too
  document.addEventListener('focusin', (e) => {
    if (!(e.target instanceof Element)) return;
    requestAnimationFrame(() => { for (let el = e.target.parentElement; el; el = el.parentElement) if (el.matches(CLIP_FRAMES)) reset(el); });
  }, true);
}

/**
 * Scroll `el` into view inside `scroller` only (Element.scrollIntoView also moves every scrollable
 * or clipping ancestor up to the page). block: 'nearest' | 'start' | 'center'; the horizontal axis
 * is brought to the nearest edge when the scroller scrolls sideways.
 */
export function scrollWithin(scroller, el, { block = 'nearest', behavior = 'auto', margin = 0 } = {}) {
  if (!scroller || !el) return;
  const s = scroller.getBoundingClientRect(), r = el.getBoundingClientRect(), cs = getComputedStyle(scroller);
  const pad = (v) => parseFloat(v) || 0; // scroll-padding keeps targets clear of sticky headers
  const axis = (pos, size, sStart, view, cur, max, mode, p0, p1) => {
    const start = pos - sStart + cur, a = margin + p0, b = margin + p1;
    let to = cur;
    if (mode === 'start') to = start - a;
    else if (mode === 'center') to = start - a - (view - a - b - size) / 2;
    else if (pos < sStart + a) to = start - a;
    else if (pos + size > sStart + view - b) to = start + size - view + b;
    return Math.max(0, Math.min(to, max));
  };
  const top = axis(r.top, r.height, s.top + scroller.clientTop, scroller.clientHeight, scroller.scrollTop,
    scroller.scrollHeight - scroller.clientHeight, block, pad(cs.scrollPaddingTop), pad(cs.scrollPaddingBottom));
  const left = scroller.scrollWidth > scroller.clientWidth + 1
    ? axis(r.left, r.width, s.left + scroller.clientLeft, scroller.clientWidth, scroller.scrollLeft,
      scroller.scrollWidth - scroller.clientWidth, 'nearest', pad(cs.scrollPaddingLeft), pad(cs.scrollPaddingRight))
    : scroller.scrollLeft;
  if (Math.abs(top - scroller.scrollTop) > 0.5 || Math.abs(left - scroller.scrollLeft) > 0.5) scroller.scrollTo({ top, left, behavior: reducedMotion() ? 'auto' : behavior });
}

export function setPressed(el, on) { if (el) el.setAttribute('aria-pressed', on ? 'true' : 'false'); }

/** Animate an element out (CSS .closing class) then hide it. */
export function hideAnimated(el, cls = 'closing', ms = 230) {
  if (!el || el.hidden) return Promise.resolve(true);
  if (reducedMotion()) { el.hidden = true; return Promise.resolve(true); }
  return new Promise((resolve) => {
    el.classList.add(cls);
    setTimeout(() => {
      // re-shown meanwhile (the opener removed the class): keep it visible
      const stillClosing = el.classList.contains(cls);
      if (stillClosing) { el.hidden = true; el.classList.remove(cls); }
      resolve(stillClosing);
    }, ms);
  });
}

/** Trigger a download of text content. */
export function downloadText(filename, text, type = 'text/plain;charset=utf-8') {
  const blob = new Blob([text], { type });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
}
